import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, RotateCcw, AlertTriangle, Loader2, Image as ImageIcon, Film, RefreshCw } from "lucide-react";
import { api, thumbUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface TrashItem {
  media_id: string;
  kind: "image" | "video" | null;
  original_filename: string | null;
  size: number | null;
  deleted_at: string;
  deleted_by: string | null;
  purge_after: string | null;
  expired: boolean;
  thumb_id: string | null;
}

const humanSize = (bytes: number | null | undefined): string => {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes; let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[u]}`;
};

/**
 * TrashBinPanel — Admin Setup surface listing every soft-deleted media asset
 * for the current workspace. Power users can restore items beyond the 30s
 * undo-toast grace window, or force-purge them immediately.
 */
export const TrashBinPanel: React.FC = () => {
  const qc = useQueryClient();
  const [confirmPurge, setConfirmPurge] = useState<string | null>(null);

  const trash = useQuery<{ items: TrashItem[]; total: number }>({
    queryKey: ["admin-media-trash"],
    queryFn: async () => (await api.get("/media/trash")).data,
    refetchInterval: 20_000,
  });

  const restore = useMutation({
    mutationFn: async (id: string) => (await api.post(`/media/${id}/undo-delete`)).data,
    onSuccess: (_d, id) => {
      toast.success("Media restored");
      qc.invalidateQueries({ queryKey: ["admin-media-trash"] });
      qc.invalidateQueries({ queryKey: ["media-lib"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Restore failed"),
  });

  const purge = useMutation({
    mutationFn: async (id: string) => (await api.post(`/media/${id}/purge-now`)).data,
    onSuccess: () => {
      toast.success("Media purged");
      setConfirmPurge(null);
      qc.invalidateQueries({ queryKey: ["admin-media-trash"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Purge failed"),
  });

  const items = trash.data?.items || [];
  const expiredCount = useMemo(() => items.filter((i) => i.expired).length, [items]);

  return (
    <div className="sg-card p-6" data-testid="admin-trash-bin">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="font-display font-bold text-lg flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-[color:var(--sb-saffron)]" /> Trash Bin
          </div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            Media soft-deleted from the Library — restore anytime or force-purge to reclaim storage.
            {expiredCount > 0 && (
              <span className="ml-1 text-amber-600 font-semibold">
                · {expiredCount} past grace window
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => qc.invalidateQueries({ queryKey: ["admin-media-trash"] })}
          disabled={trash.isFetching}
          data-testid="admin-trash-refresh"
        >
          {trash.isFetching ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {trash.isLoading ? (
        <div className="sg-skeleton h-48" />
      ) : items.length === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-10 text-center border border-dashed border-[color:var(--sg-border)] rounded-lg" data-testid="admin-trash-empty">
          The trash is empty. Deleted media appears here for 30 seconds before being purged.
        </div>
      ) : (
        <div className="divide-y divide-[color:var(--sg-border)]">
          {items.map((it) => (
            <div key={it.media_id} className="flex items-center gap-3 py-3" data-testid={`admin-trash-row-${it.media_id}`}>
              <div className="h-12 w-12 rounded-md overflow-hidden bg-[color:var(--sg-muted-bg)] flex items-center justify-center border border-[color:var(--sg-border)] shrink-0">
                {it.thumb_id ? (
                  <img
                    src={thumbUrl(it.media_id, it.thumb_id)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                  />
                ) : it.kind === "video" ? (
                  <Film className="h-4 w-4 text-[color:var(--sg-muted)]" />
                ) : (
                  <ImageIcon className="h-4 w-4 text-[color:var(--sg-muted)]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {it.original_filename || it.media_id}
                </div>
                <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                  <span>{humanSize(it.size)}</span>
                  <span>·</span>
                  <span>Deleted {new Date(it.deleted_at).toLocaleString()}</span>
                  {it.expired && (
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      <AlertTriangle className="h-3 w-3" /> Past grace
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => restore.mutate(it.media_id)}
                  disabled={restore.isPending}
                  data-testid={`admin-trash-restore-${it.media_id}`}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmPurge(it.media_id)}
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid={`admin-trash-purge-${it.media_id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Purge
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!confirmPurge} onOpenChange={(v) => !v && setConfirmPurge(null)}>
        <DialogContent className="max-w-sm" data-testid="admin-trash-purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <AlertTriangle className="h-4 w-4 text-red-600" /> Purge this media?
            </DialogTitle>
            <DialogDescription className="text-xs">
              This permanently deletes the file from storage. It cannot be restored afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPurge(null)} data-testid="admin-trash-purge-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmPurge && purge.mutate(confirmPurge)}
              disabled={purge.isPending}
              data-testid="admin-trash-purge-confirm"
            >
              {purge.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
              Purge permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
